/**
 * Google Maps initialization with dual-mode support (2D + 3D).
 * Uses the JS API Loader functional API for async library loading.
 */

import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { CLIENT_CONFIG, DEFAULT_CENTER, DEFAULT_ZOOM, DEFAULT_3D_RANGE } from '@/config';

/** View mode for the map */
export type ViewMode = '2d' | '3d';

/** Union type for map instances */
export type MapInstance = google.maps.Map | google.maps.maps3d.Map3DElement;

/** Whether the loader options have been set */
let optionsSet = false;

/** Ensure loader options are set exactly once */
function ensureOptions(): void {
  if (optionsSet) return;
  setOptions({
    key: CLIENT_CONFIG.googleMapsKey,
    v: 'weekly',
    libraries: ['marker', 'maps3d', 'places'],
  });
  optionsSet = true;
}

/** Load the Google Maps core + marker libraries */
export async function loadGoogleMaps(): Promise<google.maps.MapsLibrary> {
  ensureOptions();
  const mapsLib = await importLibrary('maps');
  await importLibrary('marker');
  return mapsLib;
}

/** Load the Google Maps 3D library (plus marker library for 3D PinElements) */
export async function loadGoogleMaps3D(): Promise<google.maps.Maps3DLibrary> {
  ensureOptions();
  await importLibrary('marker');
  return importLibrary('maps3d');
}

/** Create a 2D Google Map instance */
export async function createMap2D(container: HTMLElement): Promise<google.maps.Map> {
  const { Map } = await loadGoogleMaps();

  return new Map(container, {
    // Advanced Markers require a Map ID. Fall back to Google's public dev Map ID
    // when none is configured so markers render (set VITE_GOOGLE_MAPS_MAP_ID for production).
    mapId: CLIENT_CONFIG.googleMapsMapId || 'DEMO_MAP_ID',
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
    colorScheme: google.maps.ColorScheme.DARK,
    gestureHandling: 'greedy',
    disableDefaultUI: false,
    zoomControl: true,
    zoomControlOptions: { position: google.maps.ControlPosition.LEFT_BOTTOM },
    scaleControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
  });
}

/**
 * Apply the label/clarity mode to a 3D map: HYBRID overlays road + place labels
 * on the photorealistic imagery; SATELLITE shows the clean imagery alone (no
 * label overdraw). Switchable live without recreating the element.
 */
export function applyLabelMode(map3d: google.maps.maps3d.Map3DElement, labelsVisible: boolean): void {
  const { HYBRID, SATELLITE } = google.maps.maps3d.MapMode;
  map3d.mode = labelsVisible ? HYBRID : SATELLITE;
}

/** Create a 3D Map3DElement instance */
export async function createMap3D(container: HTMLElement): Promise<google.maps.maps3d.Map3DElement> {
  const { Map3DElement } = await loadGoogleMaps3D();

  const map3d = new Map3DElement();
  map3d.mode = google.maps.maps3d.MapMode.HYBRID;
  map3d.center = { lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng, altitude: 0 };
  map3d.range = DEFAULT_3D_RANGE;
  map3d.tilt = 0;
  map3d.heading = 0;
  map3d.style.width = '100%';
  map3d.style.height = '100%';
  map3d.style.display = 'block';

  container.appendChild(map3d);
  return map3d;
}

/** Clear all child elements from a container (used before mode switch) */
export function clearContainer(container: HTMLElement): void {
  container.innerHTML = '';
}
